import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useOrderReview, useSubmitDropship } from '@/hooks/useOrderReview'
import { ApiError } from '@/lib/api/client'
import { DropshipSubmitError } from '@/lib/api/orderReview'
import type { LineItem, OrderReview as OrderReviewData } from '@/lib/schemas/orderReview'
import { canSend, centsToString, computeTotal, thresholdState, toCents } from '@/lib/review-logic'
import { statusBadge, type StatusBadge } from '@/lib/active-status'
import type { ActiveStatus } from '@/lib/schemas/active'

const KH_PHONE = '+1 845 287 0004'
const KH_EMAIL = 'ds@dapperhorse.com'
const INTERNAL_NOTE_PLACEHOLDER =
  'Add internal note (optional)'
const OVERRIDE_NOTE_PLACEHOLDER = 'Customer has 8 orders, AOV $340, requested immediately'

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(d)
}

function inventoryStatus(item: LineItem): { icon: string; text: string; className: string } {
  switch (item.stock_state) {
    case 'IN_STOCK':
      return {
        icon: '✅',
        text: `${item.available} units at Brewster`,
        className: 'text-green-700',
      }
    case 'OOS_INCOMING':
      return {
        icon: '⚠️',
        text: `0 units — ${item.incoming} incoming`,
        className: 'text-amber-700',
      }
    case 'OOS_NO_INCOMING':
      return { icon: '⛔', text: '0 units — no incoming', className: 'text-red-700' }
  }
}

export default function OrderReview() {
  const { shopifyOrderId = '' } = useParams()
  const { data, isLoading, isError, error, refetch } = useOrderReview(shopifyOrderId)

  return (
    <div className="space-y-4">
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading order…</p>
      ) : isError ? (
        <LoadError error={error} onRetry={() => void refetch()} />
      ) : data ? (
        <ReviewForm order={data} />
      ) : null}
    </div>
  )
}

function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const is404 = error instanceof ApiError && error.status === 404
  return (
    <div className="space-y-2">
      <h1 className="text-lg font-semibold">Order Review</h1>
      <p role="alert" className="text-sm text-red-600">
        {is404
          ? 'Order not found — it may have already been processed or removed from the queue.'
          : error instanceof Error
            ? error.message
            : 'Failed to load the order.'}
      </p>
      <div className="flex gap-2">
        {!is404 && (
          <button
            type="button"
            onClick={onRetry}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        )}
        <Link
          to="/queue"
          className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
        >
          ← Back to Queue
        </Link>
      </div>
    </div>
  )
}

function ReviewForm({ order }: { order: OrderReviewData }) {
  const navigate = useNavigate()
  const submit = useSubmitDropship()

  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(order.line_items.map((li) => [li.line_item_id, li.pre_checked])),
  )
  const [overrideChecked, setOverrideChecked] = useState(false)
  const [overrideNote, setOverrideNote] = useState('')
  const [internalNote, setInternalNote] = useState('')
  const [confirm, setConfirm] = useState<{ id: string; incoming: number } | null>(null)
  const [confirmAll, setConfirmAll] = useState<{ count: number } | null>(null)

  const selectedItems = useMemo(
    () => order.line_items.filter((li) => checked[li.line_item_id]),
    [order.line_items, checked],
  )
  const allSelected =
    order.line_items.length > 0 && selectedItems.length === order.line_items.length
  const someSelected = selectedItems.length > 0 && !allSelected
  const total = useMemo(() => computeTotal(selectedItems), [selectedItems])
  const state = useMemo(
    () => thresholdState(total, order.thresholds, order.is_vip),
    [total, order.thresholds, order.is_vip],
  )

  const showOverrideSection = order.is_vip && state.state === 'VIP_OVERRIDE'
  const sendEnabled = canSend(state.state, overrideChecked, overrideNote, order.already_submitted)

  function toggle(item: LineItem) {
    const isChecked = checked[item.line_item_id]
    // Checking an incoming-PO item asks for a soft confirmation first.
    if (!isChecked && item.stock_state === 'OOS_INCOMING') {
      setConfirm({ id: item.line_item_id, incoming: item.incoming })
      return
    }
    setChecked((prev) => ({ ...prev, [item.line_item_id]: !isChecked }))
  }

  function confirmIncoming() {
    if (confirm) setChecked((prev) => ({ ...prev, [confirm.id]: true }))
    setConfirm(null)
  }

  function setAll(predicate: (item: LineItem) => boolean) {
    setChecked(Object.fromEntries(order.line_items.map((li) => [li.line_item_id, predicate(li)])))
  }

  // Check-all: deselect everything when all are selected; otherwise select all —
  // but if that would newly select any OOS_INCOMING line, ask once first so the
  // soft-confirm rule is never silently bypassed.
  function toggleAll() {
    if (allSelected) {
      setAll(() => false)
      return
    }
    const newlyIncoming = order.line_items.filter(
      (li) => li.stock_state === 'OOS_INCOMING' && !checked[li.line_item_id],
    )
    if (newlyIncoming.length > 0) {
      setConfirmAll({ count: newlyIncoming.length })
      return
    }
    setAll(() => true)
  }

  function confirmAllIncoming() {
    setAll(() => true)
    setConfirmAll(null)
  }

  function cancelAllIncoming() {
    // Select everything except the incoming-PO lines.
    setAll((li) => li.stock_state !== 'OOS_INCOMING')
    setConfirmAll(null)
  }

  function handleSend() {
    submit.mutate({
      shopify_order_id: order.shopify_order_id,
      line_item_ids: selectedItems.map((li) => li.line_item_id),
      override: showOverrideSection && overrideChecked
        ? { enabled: true, note: overrideNote.trim() }
        : null,
      internal_note: internalNote.trim() || null,
    })
  }

  if (submit.isSuccess) {
    return <SuccessPanel result={submit.data} />
  }

  return (
    <div className="space-y-6">
      {order.already_submitted && <AlreadySubmittedBanner order={order} />}

      <OrderHeader order={order} />

      <LineItemsTable
        order={order}
        checked={checked}
        onToggle={toggle}
        allSelected={allSelected}
        someSelected={someSelected}
        onToggleAll={toggleAll}
      />

      <RunningTotal count={selectedItems.length} total={total} state={state} />

      {showOverrideSection && (
        <section className="space-y-2 border border-amber-300 bg-amber-50 p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={overrideChecked}
              onChange={(e) => setOverrideChecked(e.target.checked)}
              className="mt-0.5"
            />
            <span>Override threshold — send to KH despite being below $125</span>
          </label>
          {order.vip_metrics && (
            <p className="text-xs text-amber-800">
              VIP: {order.vip_metrics.order_count} orders · AOV ${order.vip_metrics.aov}
            </p>
          )}
          {overrideChecked && (
            <textarea
              value={overrideNote}
              onChange={(e) => setOverrideNote(e.target.value)}
              placeholder={OVERRIDE_NOTE_PLACEHOLDER}
              aria-label="Override note (required)"
              rows={2}
              className="w-full border border-amber-300 px-2 py-1 text-sm"
            />
          )}
        </section>
      )}

      <section className="space-y-1">
        <label htmlFor="internal-note" className="text-sm font-medium">
          Internal note
        </label>
        <textarea
          id="internal-note"
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          placeholder={INTERNAL_NOTE_PLACEHOLDER}
          rows={2}
          className="w-full border border-gray-300 px-2 py-1 text-sm"
        />
        <p className="text-xs text-gray-400">Never sent to KH — stored on the dropship record.</p>
      </section>

      {submit.isError && <SubmitError error={submit.error} />}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/queue')}
          className="border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:text-black"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={!sendEnabled || submit.isPending}
          className="border border-gray-800 bg-gray-900 px-4 py-2 text-sm text-white hover:bg-black disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-200 disabled:text-gray-400"
        >
          {submit.isPending ? 'Sending…' : 'Send to KH Dropship →'}
        </button>
        {order.already_submitted && (
          <span className="text-xs text-amber-700">Already submitted to KH — cannot re-send.</span>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          incoming={confirm.incoming}
          onCancel={() => setConfirm(null)}
          onConfirm={confirmIncoming}
        />
      )}

      {confirmAll && (
        <ConfirmAllDialog
          count={confirmAll.count}
          onCancel={cancelAllIncoming}
          onConfirm={confirmAllIncoming}
        />
      )}
    </div>
  )
}

function AlreadySubmittedBanner({ order }: { order: OrderReviewData }) {
  const active = order.active_dropship
  const reference = active?.reference_client ?? '—'
  // active_dropship.status is a plain string on the wire; statusBadge's Record
  // lookup returns undefined at runtime for values outside ActiveStatus, so
  // guard it and fall back to the raw value.
  const badge = active
    ? (statusBadge(active.status as ActiveStatus) as StatusBadge | undefined)
    : undefined
  const status = badge?.label ?? active?.status ?? 'submitted'
  const when = active?.submitted_at ? formatDate(active.submitted_at) : null
  return (
    <div role="status" className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
      <p>
        This order was already submitted to KH as{' '}
        <span className="font-medium">{reference}</span> — {status}
        {when ? ` on ${when}` : ''}.
      </p>
      <Link to="/active" className="mt-1 inline-block underline">
        View in active dropships →
      </Link>
    </div>
  )
}

function OrderHeader({ order }: { order: OrderReviewData }) {
  const addr = order.shipping_address
  return (
    <header className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">
          Order #{order.order_number}
        </h1>
        <span className="text-sm text-gray-600">
          {order.customer_name}
          {order.is_vip && (
            <span title="VIP" aria-label="VIP" className="ml-1">
              ⭐
            </span>
          )}
        </span>
        <span className="text-sm text-gray-400 tabular-nums">
          {formatDate(order.order_date)}
        </span>
        <Form5106Badge status={order.form_5106_preview} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="text-sm">
          <div className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
            Ship to (sent to KH)
          </div>
          <div className="mt-1 text-gray-700">
            <div>{addr.name}</div>
            <div>{addr.address1}</div>
            {addr.address2 && <div>{addr.address2}</div>}
            <div>
              {addr.city}, {addr.province_code} {addr.zip}
            </div>
            <div>{addr.country_code}</div>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div>
            <div className="text-gray-700">{order.customer_phone ?? '—'}</div>
            <div className="text-xs text-gray-400">
              Team reference only — KH receives {KH_PHONE}
            </div>
          </div>
          <div>
            <div className="text-gray-700">{order.customer_email ?? '—'}</div>
            <div className="text-xs text-gray-400">
              Team reference only — never sent to KH (KH receives {KH_EMAIL})
            </div>
          </div>
        </div>
      </div>

      {order.order_note && order.order_note.trim() && (
        <div className="border border-blue-200 bg-blue-50 p-3">
          <div className="text-xs font-semibold tracking-wide text-blue-700 uppercase">
            Order notes from customer
          </div>
          <p className="mt-1 text-sm whitespace-pre-wrap text-gray-700">{order.order_note}</p>
        </div>
      )}
    </header>
  )
}

function Form5106Badge({ status }: { status: OrderReviewData['form_5106_preview'] }) {
  if (status === 'PENDING') {
    return (
      <span className="border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
        First dropship for this customer — Form 5106 will be required
      </span>
    )
  }
  return (
    <span className="border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-800">
      Form 5106 on file
    </span>
  )
}

function LineItemsTable({
  order,
  checked,
  onToggle,
  allSelected,
  someSelected,
  onToggleAll,
}: {
  order: OrderReviewData
  checked: Record<string, boolean>
  onToggle: (item: LineItem) => void
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
}) {
  const thBase =
    'px-3 py-2 text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'
  return (
    <div className="overflow-x-auto border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className={`${thBase} text-left`}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected
                }}
                onChange={onToggleAll}
                aria-label="Select all line items"
              />
            </th>
            <th className={`${thBase} text-left`}>Product</th>
            <th className={`${thBase} text-left`}>SKU</th>
            <th className={`${thBase} text-left`}>EAN</th>
            <th className={`${thBase} text-left`}>Retail</th>
            <th className={`${thBase} text-left`}>Line total</th>
            <th className={`${thBase} text-left`}>Inventory Status</th>
          </tr>
        </thead>
        <tbody>
          {order.line_items.map((item) => {
            const status = inventoryStatus(item)
            const lineTotal = centsToString(toCents(item.unit_price) * item.quantity)
            return (
              <tr key={item.line_item_id} className="border-b border-gray-100 last:border-b-0">
                <td className="px-3 py-2 align-top">
                  <input
                    type="checkbox"
                    checked={!!checked[item.line_item_id]}
                    onChange={() => onToggle(item)}
                    aria-label={`Select ${item.title}`}
                  />
                </td>
                <td className="px-3 py-2">{item.title}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{item.sku}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{item.ean ?? '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
                  ${item.unit_price}
                  <span className="text-gray-400"> × </span>
                  <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 tabular-nums">
                    {item.quantity}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-left font-medium tabular-nums">
                  ${lineTotal}
                </td>
                <td className={`px-3 py-2 whitespace-nowrap ${status.className}`}>
                  {status.icon} {status.text}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function RunningTotal({
  count,
  total,
  state,
}: {
  count: number
  total: string
  state: ReturnType<typeof thresholdState>
}) {
  const tone =
    state.state === 'OK'
      ? 'border-green-300 bg-green-50 text-green-800'
      : state.state === 'VIP_OVERRIDE'
        ? 'border-amber-300 bg-amber-50 text-amber-800'
        : state.state === 'EMPTY'
          ? 'border-gray-200 bg-gray-50 text-gray-600'
          : 'border-red-300 bg-red-50 text-red-800'

  return (
    <div className={`space-y-1 border p-3 text-sm ${tone}`}>
      <div className="font-medium">
        {state.state === 'EMPTY'
          ? state.message
          : `Selected: ${count} items | Total: $${total} — ${state.message}`}
      </div>
      <div className="text-xs text-gray-600">Dropship fee: $31.99 (flat per order)</div>
      <div className="text-xs text-gray-600">
        Customs charges: billed by KH weekly — not calculated per order
      </div>
    </div>
  )
}

function ConfirmDialog({
  incoming,
  onCancel,
  onConfirm,
}: {
  incoming: number
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm dropship of incoming item"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm space-y-4 border border-gray-300 bg-white p-4 shadow-lg">
        <p className="text-sm">Stock incoming — {incoming} units expected.</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="border border-gray-800 bg-gray-900 px-3 py-1 text-sm text-white hover:bg-black"
          >
            Yes, dropship this item
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmAllDialog({
  count,
  onCancel,
  onConfirm,
}: {
  count: number
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm dropship of incoming items"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm space-y-4 border border-gray-300 bg-white p-4 shadow-lg">
        <p className="text-sm">
          {count} items have incoming stock — dropship them anyway?
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="border border-gray-800 bg-gray-900 px-3 py-1 text-sm text-white hover:bg-black"
          >
            Yes, include them
          </button>
        </div>
      </div>
    </div>
  )
}

function SubmitError({ error }: { error: unknown }) {
  const code = error instanceof DropshipSubmitError ? error.code : undefined
  const message = error instanceof Error ? error.message : 'Submission failed.'

  if (code === 'KH_ARTICLE_NOT_FOUND') {
    return (
      <div role="alert" className="border border-red-300 bg-red-50 p-3 text-sm text-red-800">
        <div className="font-medium">Item not in KH catalogue — check EAN/SKU with KH</div>
        <div className="mt-1 text-xs">{message}</div>
      </div>
    )
  }
  if (code === 'ALREADY_SUBMITTED') {
    return (
      <div role="alert" className="border border-red-300 bg-red-50 p-3 text-sm text-red-800">
        <div>{message}</div>
        <Link to="/queue" className="mt-1 inline-block underline">
          ← Back to Queue
        </Link>
      </div>
    )
  }
  return (
    <p role="alert" className="text-sm text-red-600">
      {message}
    </p>
  )
}

function SuccessPanel({
  result,
}: {
  result: import('@/lib/schemas/orderReview').DropshipResponse
}) {
  return (
    <div className="space-y-4">
      <div className="border border-green-300 bg-green-50 p-4 text-sm text-green-900">
        <h1 className="text-lg font-semibold">✅ Sent to KH Dropship</h1>
        <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <div>
            <dt className="inline text-green-800">Reference: </dt>
            <dd className="inline font-medium">{result.reference_client}</dd>
          </div>
          <div>
            <dt className="inline text-green-800">KH status: </dt>
            <dd className="inline font-medium">{result.kh_status}</dd>
          </div>
          <div>
            <dt className="inline text-green-800">Form 5106: </dt>
            <dd className="inline font-medium">{result.form_5106_status}</dd>
          </div>
          <div>
            <dt className="inline text-green-800">Selected total: </dt>
            <dd className="inline font-medium tabular-nums">${result.selected_total}</dd>
          </div>
          <div>
            <dt className="inline text-green-800">Dropship fee: </dt>
            <dd className="inline font-medium tabular-nums">${result.dropship_fee}</dd>
          </div>
        </dl>
      </div>
      <Link
        to="/queue"
        className="inline-block border border-gray-800 px-4 py-2 text-sm hover:bg-gray-900 hover:text-white"
      >
        Back to Queue
      </Link>
    </div>
  )
}
